import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../Pagination';
import Fab from '../../Fab';
import { fmtDateTime } from '../../../utils/region';
import type { AwCtx } from '../ctx';
import { baht, btnPrimary, card, dmy, errBox, note, num, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { Dialog, Loader, actBtn, cancelBtn, emptyLine, field, isoDate, lbl, useDialogClose } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';
import { DocEntriesBtn } from '../docEntries';
import { cashApi, CASH_ERRORS } from '../../../utils/api/clientCash';
import type { CashAccountOption, CashBox, CashRow, CashRowsPage } from '../../../utils/api/clientCash';
import { settleApi } from '../../../utils/api/settlements';
import { AttachmentsBtn, useAttachmentCounts } from '../attachments';   // owner 2026-11-02: a cash row's supporting files
// ⛔ The house's deliberate cycle (Purchases.tsx ↔ ExpenseRecords.tsx ↔ Banking.tsx): the report's words (settleSkipText)
// and the month's hand-voucher dialog (SettleAckDialog) are Banking's, one of each. Used inside component bodies only.
import { SettleAckDialog, settleSkipText } from './Banking';

// ── Banking › Petty Cash (batch 11 W4 R7, P7b; design §3.4, §3.9, D19, D24, D25, D30; the W4 brief §3 P7b tasks 1, 2, 8) ──
// The CLIENT COMPANY's own petty-cash boxes — ⛔ never the firm's own petty cash (/m/pettycash, its 08:00 digest), which is
// the firm's money and is not read here. A box is one cash account of the client's chart that no bank account is paired
// with, opened on a date with a figure; its rows are what moved through it: OUT, a cash payment of the register (Record
// payment › Cash — that row is the payment's settlement source, the cash book); IN, a top-up from a bank debit line
// (Banking › Transactions › Match › "Top up petty cash" — the LINE is the top-up's source).
//  · The box's balance (opening + live in − live out) is shown BESIDE the account's book balance, with the difference
//    stated, never adjusted (D24): the opening is not posted, and anything recorded only by hand shows up there.
//  · A negative balance is an amber chip, never a refusal (D25): the books are right, a top-up is missing.
//  · Rows 100 a page, oldest first, each live row with its running balance (the server's window function), a reversed row
//    struck with its reason, who and when; each row's settlement by its own state — `ready` → "Create draft entry" (an OUT
//    row through `only.cashRowIds`, an IN row through its line's `only.bankLineIds`, no period); not in the books → why
//    (the report's words) and "Review N vouchers"; and on every row, whatever its state, the row's entries panel (b12 T5).
//  · ⛔ A read-only seat (the client's books login, the auditor) reads all of it and is drawn no write: no New box, Edit,
//    Switch off / on, Delete, Create, Confirm.
//  · ⛔ No request the server is certain to refuse: the account picker offers only `accountOptions` (the accounts the
//    server's boxAccountProblem passes — box_account is never sent), Delete is drawn only on a box with no row, the opening
//    date never past the box's first live row nor into a closed period (both said, Save held).

type TF = (key: string, opts?: Record<string, unknown>) => string;
/** A refusal of the petty cash doors by its code (aw_pc_err_<code>); anything else in the server's words. */
export const cashErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (CASH_ERRORS as readonly string[]).includes(code) ? t(`aw_pc_err_${code}`) : (String(e?.payload?.error || e?.message || '') || t('aw_pc_err_generic'));
};
/** The closed accounting period covering `date`, if any (the shell's periods). */
const closedOn = (ctx: AwCtx, date: string) => (ctx.periods || []).find(p => p.status === 'closed' && p.start_date <= date && date <= p.end_date) || null;
/** Satang typed as the books print money (2 dp), for the opening figure field. */
const satToField = (sat: number) => (Math.round(Number(sat) || 0) / 100).toFixed(2);
const fieldSat = (v: string): number | null => {
  const s = String(v ?? '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
};
/** Cancel inside the window, through the shell's one close gate (dialog.tsx useDialogClose). */
const GateCancel = ({ onClose, busy, label }: { onClose: () => void; busy?: boolean; label: string }) => {
  const close = useDialogClose(onClose);
  return <button onClick={close} disabled={busy} style={cancelBtn}>{label}</button>;
};

// ── "＋ New box" / Edit — the box's own window (full screen, the shell's FS-1 window: its Save pinned in the footer) ──────
/**
 * A new box: its name, the account (ONLY the server's `accountOptions` — a cash account no bank account is paired with and
 * no box holds, so box_account is never sent), the opening date and figure. Editing a box: its name, opening date and
 * opening figure (each audited old → new by the server); its account is shown, never changed here. The opening date
 * never moves past the box's first live row, nor into a closed period — both said, Save held. Dirty from the first edit.
 */
export function CashBoxDialog({ ctx, box, accountOptions, onClose, onSaved }: {
  ctx: AwCtx; box?: CashBox | null; accountOptions: CashAccountOption[]; onClose: () => void; onSaved: (b: CashBox) => void;
}) {
  const { t } = useTranslation();
  const edit = !!box;
  const [name, setName] = useState(box?.name || '');
  const [accountId, setAccountId] = useState(box?.accountId || (accountOptions.length === 1 ? accountOptions[0].id : ''));
  const [openingDate, setOpeningDate] = useState(box?.openingDate || '');
  const [opening, setOpening] = useState(box ? satToField(box.openingSat) : '');
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const [err, setErr] = useState('');
  // FS-12: set by the edit path only — opening the window and closing it again asks nothing.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const sat = fieldSat(opening);
  // (The W4 gate review) The closed period is asked only of a date being SET: a box whose opening (unchanged) lies in a period
  // closed since is still renamed or re-figured — the server reads the date only when it is sent, and it is sent only changed.
  const shut = isoDate(openingDate) && (!edit || openingDate !== box!.openingDate) ? closedOn(ctx, openingDate) : null;
  const pastRow = edit && !!box?.firstRowDate && isoDate(openingDate) && openingDate > String(box.firstRowDate);
  const nameOk = !!name.trim() && name.trim().length <= 80;
  const why = !nameOk ? t('aw_pc_err_name')
    : !edit && !accountOptions.some(a => a.id === accountId) ? (accountOptions.length ? t('aw_pc_pick_account_first') : t('aw_pc_no_account'))
      : !isoDate(openingDate) ? t('aw_pc_err_date')
        : shut ? t('aw_pc_err_period_closed_on', { date: dmy(openingDate), from: dmy(shut.start_date), to: dmy(shut.end_date) })
          : pastRow ? t('aw_pc_opening_after_row', { date: dmy(String(box?.firstRowDate || '')) })
            : sat === null ? t('aw_pc_err_amount') : '';
  const changed = !edit || name.trim() !== box!.name || openingDate !== box!.openingDate || sat !== box!.openingSat;
  const ready = !why && changed && !busy;
  const submit = async () => {
    if (!ready || inflight.current) return;
    inflight.current = true; setBusy(true); setErr('');
    try {
      const r = edit
        ? await cashApi.updateBox(ctx.clientId, box!.id, {
          ...(name.trim() !== box!.name ? { name: name.trim() } : {}),
          ...(openingDate !== box!.openingDate ? { openingDate } : {}),
          ...(sat !== box!.openingSat ? { openingAmount: satToField(sat as number) } : {}),
        })
        : await cashApi.createBox(ctx.clientId, { name: name.trim(), accountId, openingDate, openingAmount: satToField(sat as number) });
      setDirty(false);
      onSaved(r.box);
    } catch (e: any) { setErr(cashErr(e, t)); } finally { inflight.current = false; setBusy(false); }
  };
  const acct = edit ? `${box!.accountCode}` : '';
  return (
    <Dialog fullscreen title={edit ? t('aw_pc_dlg_edit') : t('aw_pc_dlg_new')} docNo={edit ? box!.name : undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!ready} data-pc-save style={{ ...btnPrimary, opacity: ready ? 1 : 0.5 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {edit ? t('aw_pc_save_btn') : t('aw_pc_create_btn')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }} data-pc-dialog={edit ? 'edit' : 'new'}>
        <div style={note}>{t('aw_pc_opening_hint')}</div>
        <div>
          <label style={lbl}>{t('aw_pc_name')}</label>
          <input value={name} onChange={e => ed(setName, e.target.value)} maxLength={80} style={field} data-pc-name />
        </div>
        <div>
          <label style={lbl}>{t('aw_pc_account')}</label>
          {edit ? (
            <div style={{ ...field, background: 'transparent', border: 'none', fontWeight: 700 }} data-pc-account-fixed>{acct} <span style={{ ...note, fontWeight: 400 }}>· {t('aw_pc_account_fixed')}</span></div>
          ) : (
            <>
              <select value={accountId} onChange={e => ed(setAccountId, e.target.value)} disabled={!accountOptions.length} style={field} data-pc-account>
                <option value="">{t('aw_pc_pick_account')}</option>
                {accountOptions.map(a => <option key={a.id} value={a.id} data-pc-account-option={a.id}>{a.code} · {a.name_th || a.name_en}</option>)}
              </select>
              {!accountOptions.length && <div style={{ ...note, color: '#92400E' }} data-pc-no-account>{t('aw_pc_no_account')}</div>}
            </>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          <div>
            <label style={lbl}>{t('aw_pc_opening_date')}</label>
            <input type="date" value={openingDate} onChange={e => ed(setOpeningDate, e.target.value)} style={field} data-pc-opening-date />
          </div>
          <div>
            <label style={lbl}>{t('aw_pc_opening_amount')}</label>
            <input type="number" step="0.01" min="0" value={opening} onChange={e => ed(setOpening, e.target.value)} style={field} data-pc-opening-amount />
          </div>
        </div>
        {!!why && (dirty || edit) && <div style={{ fontSize: 11.5, color: '#B42318' }} data-pc-why>{why}</div>}
        {err && <div style={errBox} data-pc-err>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── One box's card ────────────────────────────────────────────────────────────────────────────────────────────────────
function BoxCard({ b, on, ro, busy, onPick, onEdit, onActive, onDelete }: {
  b: CashBox; on: boolean; ro: boolean; busy: string; onPick: () => void; onEdit: () => void; onActive: () => void; onDelete: () => void;
}) {
  const { t } = useTranslation();
  const diff = b.bookSat === null ? null : b.balanceSat - b.bookSat;
  return (
    <div style={{ ...card, minWidth: 260, flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer',
      ...(on ? { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent)' } : {}), opacity: b.active ? 1 : 0.75 }}
      onClick={onPick} data-pc-box={b.id} data-pc-box-on={on ? '1' : undefined}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 14 }}>{b.name}</b>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{t('aw_pc_account')} {b.accountCode}</span>
        {!b.active && <span style={pillGrey} data-pc-inactive>{t('aw_pc_inactive')}</span>}
        {b.negative && <span style={pillAmber} data-pc-negative>{t('aw_pc_negative')}</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('aw_pc_opening', { date: dmy(b.openingDate), amount: baht(b.openingSat) })} · {t('aw_pc_rows_n', { n: b.rows })}</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div><div style={{ ...lbl, marginBottom: 2 }}>{t('aw_pc_balance')}</div>
          <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: b.negative ? '#B42318' : 'var(--text-primary)' }} data-pc-balance={b.balanceSat}>{b.negative ? '−' : ''}{baht(Math.abs(b.balanceSat))}</div></div>
        <div><div style={{ ...lbl, marginBottom: 2 }}>{t('aw_pc_book')}</div>
          <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }} data-pc-book={b.bookSat === null ? '' : b.bookSat}>{b.bookSat === null ? '—' : `${b.bookSat < 0 ? '−' : ''}${baht(Math.abs(b.bookSat))}`}</div></div>
      </div>
      {diff !== null && diff !== 0 && <div style={{ fontSize: 11.5, color: '#92400E' }} data-pc-difference={diff}>{t('aw_pc_difference', { amount: `${diff < 0 ? '−' : ''}${baht(Math.abs(diff))}` })}</div>}
      {!ro && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }} onClick={e => e.stopPropagation()}>
          <button type="button" onClick={onEdit} disabled={!!busy} style={actBtn} data-pc-edit={b.id}><i className="ti ti-pencil" aria-hidden /> {t('aw_pc_edit')}</button>
          <button type="button" onClick={onActive} disabled={!!busy} style={actBtn} data-pc-active={b.id}>
            <i className={busy === `act-${b.id}` ? 'ti ti-loader' : b.active ? 'ti ti-player-pause' : 'ti ti-player-play'} aria-hidden /> {b.active ? t('aw_pc_deactivate') : t('aw_pc_activate')}
          </button>
          {/* ⛔ A box with a row is never deleted (409 box_has_rows): the button is not drawn — it is switched off instead. */}
          {b.rows === 0 && <button type="button" onClick={onDelete} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} data-pc-delete={b.id}><i className={busy === `del-${b.id}` ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_pc_delete')}</button>}
        </div>
      )}
    </div>
  );
}

// ── One box's rows ────────────────────────────────────────────────────────────────────────────────────────────────────
function BoxRows({ ctx, box, ro }: { ctx: AwCtx; box: CashBox; ro: boolean }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CashRowsPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [tick, setTick] = useState(0);
  const [acking, setAcking] = useState<CashRow | null>(null);
  const clips = useAttachmentCounts(ctx.clientId, 'cash_row', (data?.rows || []).map(r => r.id));
  useEffect(() => { setPage(1); }, [box.id]);
  useEffect(() => {
    let alive = true;
    setData(null);
    cashApi.rows(ctx.clientId, box.id, page)
      .then(d => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit: 100, openingSat: box.openingSat, balanceSat: box.balanceSat }); setErr(cashErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, box.id, page, tick]);   // eslint-disable-line react-hooks/exhaustive-deps
  const create = async (r: CashRow) => {
    if (busy) return;
    setBusy(r.id); setErr('');
    try {
      // An OUT row is its own source (cash_line); an IN row's source is its statement line (the top-up's), never the row.
      await settleApi.run(ctx.clientId, { dryRun: false, only: r.direction === 'out' ? { cashRowIds: [r.id] } : { bankLineIds: [r.holder.id] } });
      ctx.bump();
    } catch (e: any) { setErr(String(e?.payload?.error || e?.message || '')); } finally { setBusy(''); }
  };
  /** What the row is: the PMT and its bills (out), or the top-up's statement line (in). */
  const what = (r: CashRow): string => r.direction === 'out'
    ? t('aw_pc_what_payment', { payNo: r.holder.docNo || '—', bills: (r.bills || []).map(b => b.billNo).join(', ') || '—' })
    : t('aw_pc_what_topup', { account: r.line?.accountNumber || '—', date: dmy(r.line?.date || r.date), description: r.line?.description || '' });
  /** The row's settlement, by its own state (the payment history's cell, for a petty cash row). */
  const settleCell = (r: CashRow): React.ReactNode => {
    const st = r.settle;
    if (!st) return '—';
    if (st.state === 'reversed' && st.previous) return <span data-pc-settle="reversed">{t('aw_epay_settle_reversed_by', { docNo: st.previous.docNo || '—', reversal: st.previous.reversalDocNo || '—' })}</span>;
    const head = <span data-pc-settle={st.state}>{t(`aw_epay_settle_${st.state}`, { docNo: st.entry?.docNo || '' })}</span>;
    if (r.state === 'reversed') return head;
    if (st.state === 'ready') {
      return (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {head}
          {!ro && (
            <button type="button" disabled={!!busy} onClick={() => create(r)} data-pc-create={r.id} style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
              <i className={busy === r.id ? 'ti ti-loader' : 'ti ti-file-plus'} aria-hidden /> {t('aw_epay_create_draft')}
            </button>
          )}
        </span>
      );
    }
    if (st.state === 'not_in_books' && st.skip) {
      const sk = st.skip;
      const n = Number(sk.candidates) || Number(sk.params?.n) || 0;
      return (
        <span style={{ display: 'inline-block', whiteSpace: 'normal', maxWidth: 340 }}>
          {head}
          <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 2 }} title={sk.why || undefined} data-pc-skip={sk.code}>{settleSkipText(t, { code: sk.code, why: sk.why, params: sk.params })}</div>
          {sk.code === 'manual_settlement_candidates' && (
            <button type="button" onClick={() => setAcking(r)} data-pc-review={r.id} style={{ ...actBtn, marginTop: 3 }}>
              <i className="ti ti-list-check" aria-hidden /> {t('aw_epay_review_vouchers', { n })}
            </button>
          )}
        </span>
      );
    }
    return head;
  };
  /** (Batch 12 tail T5, COA-D2) The row's entries panel on EVERY row — out and in, live and struck, whatever its settle
   *  state, for every seat — by the row itself (cash_line): an out row answers its own entries (a reversed one its entry
   *  and that entry's reversal), an in row (a top-up) answers `elsewhere`, pointing at the statement line it is settled on.
   *  Offered per kind, never per row (docEntries.tsx DocEntriesBtn) — the resolver says when nothing is in the books yet. */
  const entriesOf = (r: CashRow): React.ReactNode => (
    <DocEntriesBtn ctx={ctx} kind="cash_line" id={r.id} docNo={`${dmy(r.date)} ${r.direction === 'out' ? r.holder.docNo || '' : r.line?.description || ''}`.trim()}
      label={<i className="ti ti-book-2" aria-hidden />} style={{ ...actBtn, padding: '1px 6px', marginLeft: 6 }} />
  );
  const rows = data?.rows || [];
  return (
    <div>
      <div style={sectionHead}>{t('aw_pc_rows_title', { name: box.name })}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }} data-pc-rows-err>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_pc_rows_none')}</div> : (
          <table style={table} data-pc-rows={box.id}>
            <thead><tr>
              <th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_pc_col_what')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('aw_pc_col_in')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_pc_col_out')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('aw_pc_col_balance')}</th><th style={th}>{t('aw_pc_col_books')}</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const gone = r.state === 'reversed';
                const strike: React.CSSProperties = gone ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
                return (
                  <React.Fragment key={r.id}>
                    <tr data-pc-row={r.id} data-pc-dir={r.direction} data-pc-gone={gone ? '1' : undefined}>
                      <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{dmy(r.date)}</td>
                      <td style={{ ...td, ...strike }}>
                        <span style={r.direction === 'in' ? pillGreen : pillGrey}>{r.direction === 'in' ? t('aw_pc_dir_in') : t('aw_pc_dir_out')}</span> {what(r)}
                        {/* The records its payment pays (2026-11-02, Automatic Expense Number), beside the bills' own numbers above. */}
                        {!!(r.bills || []).some(b => b.expenseNo) && <div data-pc-exp-nos={r.id} title={t('aw_er_col_no')} style={{ marginTop: 3, fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{(r.bills || []).map(b => b.expenseNo).filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td style={{ ...num, ...strike }}>{r.direction === 'in' ? baht(r.amountSat) : ''}</td>
                      <td style={{ ...num, ...strike }}>{r.direction === 'out' ? baht(r.amountSat) : ''}</td>
                      <td style={{ ...num, color: (r.balanceSat ?? 0) < 0 ? '#B42318' : undefined }} data-pc-row-balance={r.balanceSat === null ? '' : r.balanceSat}>
                        {r.balanceSat === null ? '' : `${r.balanceSat < 0 ? '−' : ''}${baht(Math.abs(r.balanceSat))}`}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}><span style={strike}>{settleCell(r)}</span>{entriesOf(r)}
                        {/* A cash row's supporting files and what it is linked to (owner 2026-11-02), as a statement line's. */}
                        <AttachmentsBtn clientId={ctx.clientId} kind="cash_row" id={r.id} docNo={`${dmy(r.date)} ${r.direction === 'in' ? t('aw_pc_dir_in') : t('aw_pc_dir_out')}`} count={clips.counts[r.id]} onClosed={clips.refresh} />
                      </td>
                    </tr>
                    {gone && (
                      <tr><td colSpan={6} style={{ ...td, paddingTop: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }} data-pc-reversed={r.id}>
                        {t('aw_pc_reversed', { by: r.reversedByName || '—', at: r.reversedAt ? fmtDateTime(r.reversedAt) : '—', reason: r.reverseReason || '—' })}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data && <div data-pc-pager><Pagination page={data.page || page} total={data.total} limit={data.limit || 100} onPage={setPage} /></div>}
      {acking && (
        <SettleAckDialog ctx={ctx} source={acking.direction === 'out' ? { kind: 'cash_line', id: acking.id } : { kind: 'bank_line', id: acking.holder.id }}
          line={{ date: acking.date, amount: acking.amountSat / 100 }} onClose={() => setAcking(null)}
          onDone={remaining => { if (!remaining) setAcking(null); setTick(x => x + 1); }} />
      )}
    </div>
  );
}

// ── The pane ──────────────────────────────────────────────────────────────────────────────────────────────────────────
export function PettyCashPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const ro = isReadOnlySeat(ctx);
  const [params, setParams] = useSearchParams();
  // The entries panel's Generate link (docEntries.tsx) hands over ?box=<id>: read when the pane mounts, then taken off the
  // URL once the periods have answered (the TransactionsPane rule — the shell may remount the pane right after a deep link).
  const [sel, setSel] = useState(() => params.get('box') || '');
  useEffect(() => {
    if (ctx.periodsLoading || !params.has('box')) return;
    setParams(p => { const n = new URLSearchParams(p); n.delete('box'); return n; }, { replace: true });
  }, [ctx.periodsLoading, params, setParams]);
  const [data, setData] = useState<{ boxes: CashBox[]; accountOptions: CashAccountOption[] } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [dlg, setDlg] = useState<{ kind: 'new' } | { kind: 'edit'; box: CashBox } | null>(null);
  useEffect(() => {
    let alive = true;
    cashApi.boxes(ctx.clientId)
      .then(d => {
        if (!alive) return;
        setData(d); setErr('');
        setSel(cur => (cur && d.boxes.some(b => b.id === cur)) ? cur : (d.boxes[0]?.id || ''));
      })
      .catch((e: any) => { if (alive) { setData({ boxes: [], accountOptions: [] }); setErr(cashErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (key: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(key); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(cashErr(e, t)); } finally { setBusy(''); }
  };
  const boxes = data?.boxes || [];
  const box = boxes.find(b => b.id === sel) || null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={note} data-pc-note>{t('aw_pc_note')}</div>
      {err && <div style={errBox} data-pc-err>{err}</div>}
      {data === null ? <Loader /> : !boxes.length ? (
        <div style={{ ...card, ...emptyLine }} data-pc-none>{t('aw_pc_none')}</div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {boxes.map(b => (
            <BoxCard key={b.id} b={b} on={b.id === sel} ro={ro} busy={busy} onPick={() => setSel(b.id)}
              onEdit={() => setDlg({ kind: 'edit', box: b })}
              onActive={() => act(`act-${b.id}`, () => cashApi.setActive(ctx.clientId, b.id, !b.active))}
              onDelete={() => { if (window.confirm(t('aw_pc_delete_confirm', { name: b.name }))) act(`del-${b.id}`, () => cashApi.deleteBox(ctx.clientId, b.id)); }} />
          ))}
        </div>
      )}
      {box && <BoxRows ctx={ctx} box={box} ro={ro} />}
      {!ro && <Fab onClick={() => setDlg({ kind: 'new' })} title={t('aw_pc_new_box')} />}
      {!ro && dlg?.kind === 'new' && data && (
        <CashBoxDialog ctx={ctx} accountOptions={data.accountOptions} onClose={() => setDlg(null)} onSaved={b => { setDlg(null); setSel(b.id); ctx.bump(); }} />
      )}
      {!ro && dlg?.kind === 'edit' && data && (
        <CashBoxDialog ctx={ctx} box={dlg.box} accountOptions={data.accountOptions} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); ctx.bump(); }} />
      )}
    </div>
  );
}
